import React, { useCallback, useEffect, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { BsArrowDown, BsDownload } from "react-icons/bs";
import { toast } from "react-toastify";
import ButtonImage from "../../components/UI/Button/ButtonImage";
import FileDownloader from "../../components/UI/Button/FileDownloader";
import TextSpinner from "../../components/UI/Spinner/TextSpinner";
import useApiClient, { apiRoutes } from "../../hooks/apiClient";
import AndamentoOsDetalhamento from "./AndamentoOsDetalhamento";
import DateTime from "../../components/Formatters/DateTime";
import CustomExpanderIcon from "../../components/UI/DataTable/CustomExpanderIcon";
import useAndamentoOsDocumento from "./AndamentoOS/hooks/useAndamentoOSDocumento";
import useAndamentoCienciaDocumento from "./AndamentoOS/hooks/useAndamentoCienciaDocumento";
import useAndamentoDesignaDocumento from "./AndamentoOS/hooks/useAndamentoDesignaDocumento";
import useGerarAutoInfracaoSigat from "./AndamentoOS/hooks/useGerarDocAutoInfracaoSigat";
import constants from "../Constants";

const Cell = (description) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{description}</Tooltip>} data-tag="allowRowEvents">
    <Button
      style={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: "#858796",
        textDecoration: "none",
      }}
      variant="link"
      data-tag="allowRowEvents"
    >
      {description}
    </Button>
  </OverlayTrigger>
);

const andamentoOsDoc = { exibir: () => {} };
const andamentoCienciaDoc = { exibir: () => {} };
const andamentoDesignaDoc = { exibir: () => {} };
const autoInfracaoSigatDoc = { exibir: () => {} };

const tipoDocumento = (row) => {
  if (row.sgAtividade === "CIORDSERAU") {
    andamentoCienciaDoc.exibir(row);
  } else if (row.sgAtividade === "DIDESAUDOS") {
    andamentoDesignaDoc.exibir(row);
  } else if (row.sgAtividade === "AUDGEIMPAI") {
    autoInfracaoSigatDoc.exibir(row);
  } else {
    andamentoOsDoc.exibir(row);
  }
};

const getTooltipDocumento = (row, doc, index) => {
  const contexto = doc.contextoGrupo || "";

  // Regra AUAPDOREAF
  if (row.sgAtividade === "AUAPDOREAF") {
    if (contexto === "Demonstrativo Ocorrencias") return "Demonstrativo";
    if (contexto === "Resumo Ocorrencias") return "Resumo";
    if (contexto === "Atividade") return "Resultado da Ação";
  }

  if (row.sgAtividade === "AUDGEIMPAI") {
    return "Auto de Infração";
  }

  // Regra Atividade
  if (contexto === "Atividade") {
    return row.sgAtividade === "AI1" ? "Relat. Circunstanciado" : "Visualizar Documento";
  }

  // Padrão
  return contexto || `Documento ${index + 1}`;
};

const columns = [
  {
    width: "55px",
    selector: (row) => <CustomExpanderIcon row={row} emptyWhen={(item) => (item.anexos?.length ?? 0) === 0} />,
  },
  {
    name: "Andamento",
    selector: "andamento",
    maxWidth: "14%",
    center: true,
    sortable: true,
    cell: (row) => <DateTime value={row.andamento} stringFormat="dd/MM/yyyy HH:mm:ss" />,
  },
  {
    name: "Atividade",
    selector: "atividade",
    sortable: true,
    cell: (row) => Cell(row.atividade),
  },
  {
    name: "Origem",
    selector: "origem",
    maxWidth: "5%",
    center: true,
    sortable: true,
  },
  {
    name: "Situação",
    selector: "situacao",
    maxWidth: "8%",
    center: true,
    sortable: true,
  },
  {
    name: "Tempo Utilizado",
    selector: "tempoUtilizado",
    center: true,
    maxWidth: "20%",
  },
  {
    name: "Usuário",
    selector: "usuario",
    maxWidth: "20%",
  },
  {
    name: "Ações",
    center: true,
    maxWidth: "1%",
    cell: (row) => {
      // 🔹 Caso NÃO tenha documentos
      if (!row.documentos || row.documentos.length === 0) {
        return (
          <ButtonImage
            className="btn-lg text-success pa-0 p-0"
            buttonImage={
              <BsDownload
                size="16"
                color={
                  row.comunicado > 0 ||
                  row.sgAtividade === "CIORDSERAU" ||
                  row.sgAtividade === "DIDESAUDOS" ||
                  row.sgAtividade === "AUDGEIMPAI"
                    ? "#e37023"
                    : "#888"
                }
              />
            }
            tooltipImage={
              row.comunicado > 0 ||
              row.sgAtividade === "CIORDSERAU" ||
              row.sgAtividade === "DIDESAUDOS" ||
              row.sgAtividade === "AUDGEIMPAI"
                ? "Visualizar Documento"
                : "Não foi encontrado documento"
            }
            onClick={() => tipoDocumento(row)}
            disabled={
              row.comunicado === 0 &&
              row.sgAtividade !== "CIORDSERAU" &&
              row.sgAtividade !== "DIDESAUDOS" &&
              row.sgAtividade !== "AUDGEIMPAI"
            }
          />
        );
      }

      // 🔹 Caso TENHA um ou mais documentos
      return (
        <div style={{ display: "flex", gap: "6px" }}>
          {row.documentos.map((doc, index) => (
            <FileDownloader
              key={index}
              binario={doc.documento}
              disabled={row.situacao === "Cancelada" || doc.situacaoDoc === "C"}
              tooltip={
                row.situacao === "Cancelada" || doc.situacaoDoc === "C"
                  ? "Documento Cancelado"
                  : getTooltipDocumento(row, doc, index)
              }
            />
          ))}
        </div>
      );
    },
  },
];

const AndamentoOs = ({ numOs, sqOs, inscEstadual, dadosOs, pageSizeRows = 10 }) => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizeRows);
  const [pageNumber, setPageNumber] = useState(1);
  const setSelection = useState({})[1];

  const { setDocParams } = useAndamentoOsDocumento();
  andamentoOsDoc.exibir = (row) => {
    setDocParams({ sqAcaoFiscal: row.sqAcaoFiscal, sqComunicado: row.comunicado, sqDesdoc: row.sqDesdoc });
  };

  const { setDocCienciaParams } = useAndamentoCienciaDocumento();
  andamentoCienciaDoc.exibir = (row) => {
    setDocCienciaParams({ sqAcaoFiscal: row.sqAcaoFiscal });
  };

  const { setDocDesignaParams } = useAndamentoDesignaDocumento();
  andamentoDesignaDoc.exibir = (row) => {
    setDocDesignaParams({ sqOrdemServico: sqOs, nuInscricaoEstadual: inscEstadual, sqAcaoFiscal: row.sqAcaoFiscal });
  };

  const { handleImprimirDocumento } = useGerarAutoInfracaoSigat({ dadosOs });

  autoInfracaoSigatDoc.exibir = (row) => {
    handleImprimirDocumento(row);
  };

  const { request: requestAndamento, data, errors: error, isLoading } = useApiClient();

  const handlePageChange = useCallback((newPageNumber) => {
    setPageNumber(newPageNumber);
  }, []);

  const handlePerRowsChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  }, []);

  useEffect(() => {
    if (numOs !== "" || numOs !== undefined) {
      const values = {
        acaoFiscal: numOs,
        pageSize,
        pageNumber,
      };

      requestAndamento(apiRoutes.fiscalizacao.ordemservico.andamentoOs, values);
    }
  }, [requestAndamento, numOs, pageSize, pageNumber]);

  useEffect(() => {
    if (data && data.result) {
      if (data.result.length && !data.result[0].totalRows) {
        console.error(`[DEV] A propriedade "totalRows" é obrigatória nos resultados paginados.`);
        return;
      }

      const preRows = data.result.map((item) => {
        const disabled = !(item.anexos?.length > 0);
        return { ...item, disabled };
      });

      setRows(preRows || []);
      setTotalRows(data.result.length ? data.result[0].totalRows : 0);
    }

    if (error) {
      toast.error(error.message);
    }
  }, [data, error]);

  return (
    <div style={{ width: "100%" }}>
      <DataTable
        noHeader
        keyField="sqAndamento"
        columns={columns}
        data={rows}
        expandableRows
        expandableRowsHideExpander
        expandOnRowClicked
        onRowExpandToggled={(expanded, row) => {
          setRows((curr) => {
            const itemIndex = curr.findIndex(
              (value) => value.sqAtividade === row.sqAtividade && value.sqAndamento === row.sqAndamento,
            );
            const items = [...curr];
            items[itemIndex] = { ...curr[itemIndex], expanded };
            return items;
          });
        }}
        expandableRowsComponent={AndamentoOsDetalhamento}
        progressPending={isLoading}
        noDataComponent="Nenhum registro para exibir"
        progressComponent={<TextSpinner />}
        defaultSortFieldId={1}
        sortIcon={<BsArrowDown />}
        pagination
        sortable
        paginationTotalRows={totalRows}
        selectableRows={false}
        onSelectedRowsChange={(state) => {
          setSelection(state.selectedRows);
        }}
        pointerOnHover
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationComponentOptions={constants.paginationComponentOptions}
        paginationPerPage={pageSize}
      />
    </div>
  );
};

export default AndamentoOs;
